import { useCallback, useState } from 'react';
import toast from 'react-hot-toast';
import { useAppDispatch, useAppSelector } from '../stores/AppStore';
import {
  resetSignUp,
  selectSessionId,
  selectLicenseCount,
  setStep,
} from '../features/signUp/signUpSlice';
import { useLazyGetSessionStatusQuery } from '../services/apiSlice';

interface UseSessionStatusCheckReturn {
  isValidating: boolean;
  validateAndProceed: (onValid: () => void) => Promise<void>;
  validateOnMount: () => Promise<boolean>;
}

/**
 * Hook for validating signup session status before proceeding to next steps.
 * Prevents duplicate thinker creation by detecting completed or expired sessions.
 */
const useSessionStatusCheck = (): UseSessionStatusCheckReturn => {
  const [isValidating, setIsValidating] = useState(false);
  const dispatch = useAppDispatch();
  const sessionId = useAppSelector(selectSessionId);
  const licenseCount = useAppSelector(selectLicenseCount);
  const [getSessionStatus] = useLazyGetSessionStatusQuery();

  const handleInvalidSession = useCallback(
    (reason: 'completed' | 'expired' | 'not_found') => {
      localStorage.removeItem('signUp-state');

      if (reason === 'completed') {
        // Set step to final step to show SubscriptionConfirm (success page)
        // SubscriptionConfirm handles the redirect based on auth status
        const finalStep = 4 + licenseCount * 2;
        dispatch(setStep(finalStep));
      } else {
        // Expired/not found - reset to step 0 and show error
        dispatch(resetSignUp(0));
        toast.error('Session expired. Please start a new signup.', {
          id: 'session-invalid',
          duration: 7000,
        });
      }
    },
    [dispatch, licenseCount],
  );

  /**
   * Validates session and proceeds with the callback if valid.
   * Shows loading spinner while validating.
   */
  const validateAndProceed = useCallback(
    async (onValid: () => void) => {
      if (!sessionId) {
        // No session ID means this is a fresh signup, allow proceeding
        onValid();
        return;
      }

      setIsValidating(true);
      try {
        const result = await getSessionStatus(sessionId).unwrap();

        if (result.isCompleted) {
          handleInvalidSession('completed');
          return;
        }

        // Session is valid, proceed
        onValid();
      } catch (error: any) {
        // Session not found or error - treat as expired
        if (error?.status === 404) {
          handleInvalidSession('not_found');
        } else {
          // For other errors, log and allow proceeding (fail open)
          console.error('Session status check failed:', error);
          onValid();
        }
      } finally {
        setIsValidating(false);
      }
    },
    [sessionId, getSessionStatus, handleInvalidSession],
  );

  /**
   * Validates session on component mount.
   * Returns true if session is valid, false otherwise.
   */
  const validateOnMount = useCallback(async (): Promise<boolean> => {
    if (!sessionId) {
      // No session ID means this is a fresh signup
      return true;
    }

    try {
      const result = await getSessionStatus(sessionId).unwrap();

      if (result.isCompleted) {
        handleInvalidSession('completed');
        return false;
      }

      return true;
    } catch (error: any) {
      if (error?.status === 404) {
        handleInvalidSession('not_found');
        return false;
      }
      // For other errors, allow proceeding (fail open)
      console.error('Session status check failed on mount:', error);
      return true;
    }
  }, [sessionId, getSessionStatus, handleInvalidSession]);

  return {
    isValidating,
    validateAndProceed,
    validateOnMount,
  };
};

export default useSessionStatusCheck;
